import { useState } from 'react'
import type { DetectionProfile } from '@/features/detection/domain/profile'
import { allRulesOn, ruleInfo, type EngineRules, type RuleId } from '@/features/detection/domain/rules'
import { sanitizeOverride, type ProfileOverride } from '../../application/profileOverride'
import { draftOf, profileFields, profileFromDraft, radiusFields, type ProfileDraft, type ProfileField } from '../profileDraft'
import { FloatingWindow } from './FloatingWindow'

// Fenêtre « Profil » de calibration (TEMPORAIRE, jamais en production) : tous les paramètres du profil et un
// interrupteur par règle. S'applique au moteur de cette tablette seulement, en observation; jamais envoyé au serveur.

interface Props {
  /** Profil figé par le serveur pour cette mission; `null` : aucun (base Démo v0.3). */
  serverProfile: DetectionProfile | null
  /** Base de secours quand le serveur n'a aucun profil. */
  fallback: DetectionProfile
  override: ProfileOverride | null
  onApply: (override: ProfileOverride | null) => void
  onClose: () => void
}

type EditableMode = 'OFF' | 'OBSERVATION'

export function ProfileWindow({ serverProfile, fallback, override, onApply, onClose }: Props) {
  const base = override?.profile ?? serverProfile ?? fallback
  // Sans calibration en cours, « Appliquer » met le moteur en observation, même si le serveur est en OFF.
  const [mode, setMode] = useState<EditableMode>(override?.profile.mode === 'OFF' ? 'OFF' : 'OBSERVATION')
  const [draft, setDraft] = useState<ProfileDraft>(() => draftOf(base))
  const [rules, setRules] = useState<EngineRules>(() => override?.rules ?? allRulesOn)
  const [errors, setErrors] = useState<string[]>([])
  const [applied, setApplied] = useState(false)

  const field = (name: ProfileField) => (
    <label key={name}>
      {profileFields[name]}
      <input
        inputMode="decimal"
        value={draft[name]}
        onChange={(event) => {
          setDraft((current) => ({ ...current, [name]: event.target.value }))
          setApplied(false)
        }}
      />
    </label>
  )
  const toggle = (id: RuleId) => {
    setRules((current) => ({ ...current, [id]: !current[id] }))
    setApplied(false)
  }
  const apply = () => {
    const result = profileFromDraft(base, mode, draft)
    if (!result.ok) {
      setErrors(result.errors)
      return
    }
    setErrors([])
    onApply(
      sanitizeOverride(
        result.profile,
        rules,
        serverProfile ? { id: serverProfile.id, version: serverProfile.version } : null,
      ),
    )
    setApplied(true)
  }
  const reset = () => {
    const source = serverProfile ?? fallback
    setDraft(draftOf(source))
    setRules(allRulesOn)
    setMode(source.mode === 'OFF' ? 'OFF' : 'OBSERVATION')
    setErrors([])
    onApply(null)
    setApplied(true)
  }

  return (
    <FloatingWindow
      title="PROFIL (calibration)"
      label="Profil de détection"
      storageKey="reca-operator-v3.profile-window"
      initial={{ x: 16, y: 72 }}
      width={460}
      className="op-profile"
      onClose={onClose}
    >
      <div>
        Profil du serveur :{' '}
        {serverProfile ? `${serverProfile.label} v${String(serverProfile.version)} (${serverProfile.mode})` : 'aucun'}
      </div>
      <div className="op-debug__warn">
        {override ? 'Modifié sur cette tablette' : 'Non modifié'} — jamais envoyé au serveur, observation seulement.
        {!serverProfile ? ' Base : Démo v0.3 (non homologuée).' : ''}
      </div>

      <div className="op-profile__section">
        <label className="op-profile__rule">
          Mode
          <select
            value={mode}
            onChange={(event) => {
              setMode(event.target.value === 'OFF' ? 'OFF' : 'OBSERVATION')
              setApplied(false)
            }}
          >
            <option value="OBSERVATION">OBSERVATION</option>
            <option value="OFF">OFF</option>
          </select>
        </label>
        <div className="op-profile__fields">{radiusFields.map(field)}</div>
      </div>

      {ruleInfo.map((rule) => (
        <div key={rule.id} className={`op-profile__section${rules[rule.id] ? '' : ' op-profile__section--off'}`}>
          <label className="op-profile__rule">
            <input type="checkbox" checked={rules[rule.id]} onChange={() => {
              toggle(rule.id)
            }} />
            {rule.label}
          </label>
          {!rules[rule.id] ? <div className="op-debug__warn">Coupée : {rule.whenOff}</div> : null}
          {rule.parameters.length > 0 ? (
            <div className="op-profile__fields">
              {rule.parameters.filter((name): name is ProfileField => name in profileFields).map(field)}
            </div>
          ) : null}
        </div>
      ))}

      {errors.length > 0 ? (
        <ul className="op-debug__warn">
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      ) : null}
      <div className="op-profile__actions">
        <button type="button" className="op-profile__apply" onClick={apply}>
          Appliquer
        </button>
        <button type="button" onClick={reset}>
          Revenir au profil du serveur
        </button>
        {applied ? <span role="status">Appliqué au moteur.</span> : null}
      </div>
    </FloatingWindow>
  )
}
